import React, { createContext, useState, useEffect, useCallback } from "react";
import { message } from "antd";
import axiosInstance from "../utils/axiosInstance";

export const ProductContext = createContext();

export function ProductProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [sortType, setSortType] = useState("");

  // ✅ Debounce search (VERY IMPORTANT)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchProducts = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Unauthorized");

        const res = await axiosInstance.get("product/products-listing", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page,
            search: debouncedSearch || undefined, // ✅ FIXED
            sort: sortType || undefined,
          },
        });

        const { success, products: pageItems, pagination: meta } = res.data;
        if (!success) throw new Error("API returned failure");

        const formatted = pageItems.map((p) => ({
          id: p.id,
          product_name: p.product_name,
          description: p.dataValues?.description_preview || p.description,
          status: p.status,
          stock: p.stock,
          types: Array.isArray(p.types) ? p.types : p.types ? [p.types] : [],
          sku: p.sku,
          product_image: p.product_image || null,
          factory_price: p.factory_price,
          price_euro: p.price_euro,
          isInventoryPart: p.isInventoryPart || false,
        }));

        setProducts(formatted);
        setPagination({
          current: meta.currentPage,
          pageSize: meta.pageSize ?? 8,
          total: meta.totalProducts,
        });
      } catch (err) {
        if (err.response?.status === 404) {
          setProducts([]);
          setPagination({
            current: 1,
            pageSize: 10,
            total: 0,
          });
        } else {
          message.error(err.response?.data?.error || err.message || "Load failed");
        }
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, sortType]
  );

  // ✅ MAIN SEARCH + SORT TRIGGER
  useEffect(() => {
    fetchProducts(1);
  }, [debouncedSearch, sortType]);

  return (
    <ProductContext.Provider
      value={{
        products,
        loading,
        pagination,
        fetchProducts,
        setPagination,
        searchTerm,
        setSearchTerm,
        sortType,
        setSortType,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}
